import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Package,
  Plus,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  CurrencyDollar,
  Sparkle,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useAppNavigate } from '@/hooks/useAppNavigate'

type PackagesStepProps = {
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  hasPackages: boolean
  onRefresh: () => Promise<void>
}

export function PackagesStep({
  onNext,
  onBack,
  onSkip,
  hasPackages,
  onRefresh,
}: PackagesStepProps) {
  const { t } = useTranslation('onboarding')
  const navigate = useAppNavigate()
  
  const [showForm, setShowForm] = useState(false)
  const [creating, setCreating] = useState(false)
  const [packageData, setPackageData] = useState({
    title: '',
    description: '',
    price: '',
  })

  const handleCreatePackage = async () => {
    if (!packageData.title || !packageData.price) {
      toast.error(t('packages.validation.required'))
      return
    }

    const priceValue = parseFloat(packageData.price)
    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error(t('packages.validation.invalidPrice'))
      return
    }

    try {
      setCreating(true)
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
      
      // Get tenant currency
      const settingsResponse = await fetch(`${apiUrl}/api/tenants/settings`, {
        credentials: 'include',
      })
      const settings = await settingsResponse.json()
      
      const response = await fetch(`${apiUrl}/api/packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          title: packageData.title,
          description: packageData.description || '',
          priceCents: Math.round(priceValue * 100),
          currency: settings.currency || 'USD',
        }),
      })

      if (response.ok) {
        toast.success(t('packages.created'))
        setShowForm(false)
        setPackageData({ title: '', description: '', price: '' })
        await onRefresh()
      } else {
        const error = await response.json()
        toast.error(error.message || t('packages.error'))
      }
    } catch (error) {
      console.error('Failed to create package:', error)
      toast.error(t('packages.error'))
    } finally {
      setCreating(false)
    }
  }

  const handleGoToPackages = () => {
    navigate('packages')
  }

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F08A24] to-[#f9a84c] mb-4">
          <Package weight="bold" className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
          {t('packages.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('packages.subtitle')}
        </p>
      </div>

      {/* Status or Form */}
      <div className="max-w-lg mx-auto w-full">
        {hasPackages && !showForm ? (
          /* Already has packages */
          <Card className="border-green-500/50 bg-green-50/50 dark:bg-green-950/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/50">
                  <CheckCircle weight="fill" className="w-6 h-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-green-800 dark:text-green-200">
                    {t('packages.alreadyCreated')}
                  </h4>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {t('packages.canAddMore')}
                  </p>
                </div>
                <Button
                  onClick={handleGoToPackages}
                  variant="outline"
                  size="sm"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                >
                  {t('packages.viewPackages')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : !showForm ? (
          /* Options to create or skip */
          <div className="space-y-4">
            <Card
              className={cn(
                'cursor-pointer transition-all duration-300',
                'hover:border-[#F08A24]/50 hover:shadow-md'
              )}
              onClick={() => setShowForm(true)}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#F08A24]/10">
                    <Plus weight="bold" className="w-6 h-6 text-[#F08A24]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#0B2A4A] dark:text-white">
                      {t('packages.createFirst')}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('packages.createFirstDesc')}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer transition-all duration-300 hover:border-gray-300 hover:shadow-sm"
              onClick={handleGoToPackages}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800">
                    <Sparkle weight="bold" className="w-6 h-6 text-gray-500" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-200">
                      {t('packages.goToPage')}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('packages.goToPageDesc')}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Quick create form */
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-[#0B2A4A] dark:text-white">
                    {t('packages.quickCreate')}
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowForm(false)}
                    className="text-gray-400"
                  >
                    {t('cancel')}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">{t('packages.form.title')}</Label>
                  <Input
                    id="title"
                    placeholder={t('packages.form.titlePlaceholder')}
                    value={packageData.title}
                    onChange={(e) =>
                      setPackageData((prev) => ({ ...prev, title: e.target.value }))
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    {t('packages.form.description')}
                  </Label>
                  <Textarea
                    id="description"
                    placeholder={t('packages.form.descriptionPlaceholder')}
                    value={packageData.description}
                    onChange={(e) =>
                      setPackageData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">{t('packages.form.price')}</Label>
                  <div className="relative">
                    <CurrencyDollar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-9"
                      value={packageData.price}
                      onChange={(e) =>
                        setPackageData((prev) => ({ ...prev, price: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <Button
                  onClick={handleCreatePackage}
                  disabled={creating || !packageData.title || !packageData.price}
                  className="w-full bg-[#F08A24] hover:bg-[#e07a14] text-white"
                >
                  {creating ? t('creating') : t('packages.form.create')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-10 max-w-lg mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <div className="flex items-center gap-3">
          <Button
            onClick={onSkip}
            variant="ghost"
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
          >
            {t('skipStep')}
          </Button>

          <Button
            onClick={onNext}
            className="bg-[#F08A24] hover:bg-[#e07a14] text-white"
          >
            {t('continue')}
            <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
